// SearchSelect — a single searchable dropdown (combobox), WEL-106.
//
// Replaces the old two-control property picker ("Filter properties" text box +
// a separate <select>) with one control, like AppFolio's property picker:
// click (or focus) to open the full list, type to filter it in place, pick an
// option directly. Selection only changes on an explicit pick — typing is just
// a query, and closing without picking reverts the text to the selected
// option's label.
//
// Accessibility: ARIA 1.2 combobox pattern — input[role=combobox] with
// aria-expanded/aria-controls/aria-activedescendant over a role=listbox popup.
// ArrowUp/Down (and Home/End) move the highlight, Enter picks, Escape closes,
// Tab / clicking away closes without picking. The popup reuses the design
// system's .menu/.menu-item primitives (see .combobox-* in index.css).

import { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchSelectOption {
  value: string;
  /** Primary text, e.g. the property name. */
  label: string;
  /** Secondary text, e.g. the address — shown muted and searched too. */
  sublabel?: string | null;
}

interface SearchSelectProps {
  /** Id for the input, so a <label htmlFor=…> can point at it. */
  id: string;
  options: SearchSelectOption[];
  /** Selected option value; '' = nothing selected. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Shown when the query matches nothing. */
  emptyText?: string;
}

/** Display text for an option — mirrors the old `<option>` "name — address". */
function optionText(opt: SearchSelectOption): string {
  return opt.sublabel ? `${opt.label} — ${opt.sublabel}` : opt.label;
}

export function SearchSelect({
  id,
  options,
  value,
  onChange,
  placeholder,
  disabled = false,
  emptyText = 'No matches',
}: SearchSelectProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  // null = not filtering: the input shows the selected option's label.
  // A string (even '') = the user is typing; filter the list by it.
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        (o.sublabel ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const displayText = query !== null ? query : selected ? optionText(selected) : '';

  const openList = () => {
    if (disabled || open) return;
    setOpen(true);
    // Land the highlight on the current selection so Enter re-confirms it.
    const idx = selected ? filtered.findIndex((o) => o.value === selected.value) : -1;
    setActiveIndex(idx >= 0 ? idx : 0);
  };

  const closeList = () => {
    setOpen(false);
    setQuery(null); // revert any un-picked typing to the selected label
  };

  const pick = (opt: SearchSelectOption) => {
    onChange(opt.value);
    setOpen(false);
    setQuery(null);
    inputRef.current?.focus();
  };

  // Keep the highlight inside the list when filtering shrinks it.
  useEffect(() => {
    setActiveIndex((i) => (filtered.length === 0 ? 0 : Math.min(i, filtered.length - 1)));
  }, [filtered.length]);

  // Keep the highlighted option scrolled into view during keyboard nav.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, filtered]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList();
        else if (filtered.length > 0) setActiveIndex((i) => (i + 1) % filtered.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList();
        else if (filtered.length > 0) {
          setActiveIndex((i) => (i - 1 + filtered.length) % filtered.length);
        }
        break;
      case 'Home':
        if (open && filtered.length > 0) {
          e.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open && filtered.length > 0) {
          e.preventDefault();
          setActiveIndex(filtered.length - 1);
        }
        break;
      case 'Enter':
        if (open) {
          e.preventDefault();
          const active = filtered[activeIndex];
          if (active) pick(active);
        }
        break;
      case 'Escape':
        if (open) {
          // Only swallow Escape while the list is open, so a parent modal's
          // own Escape handling still works once the list is closed.
          e.preventDefault();
          e.stopPropagation();
          closeList();
        }
        break;
      case 'Tab':
        if (open) closeList();
        break;
      default:
        break;
    }
  };

  const activeId =
    open && filtered.length > 0 && filtered[activeIndex]
      ? `${id}-option-${activeIndex}`
      : undefined;

  return (
    <div
      ref={rootRef}
      className="combobox"
      onBlur={(e) => {
        // Close when focus leaves the whole widget (input + list).
        if (!rootRef.current?.contains(e.relatedTarget as Node | null)) closeList();
      }}
    >
      <input
        ref={inputRef}
        id={id}
        type="text"
        className="input combobox-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${id}-listbox`}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        disabled={disabled}
        value={displayText}
        onKeyDown={onKeyDown}
        onChange={(e) => {
          setQuery(e.target.value);
          setActiveIndex(0);
          if (!open) setOpen(true);
        }}
        onFocus={(e) => {
          // Select-all so typing replaces the previous "name — address" text.
          e.target.select();
          openList();
        }}
        onClick={(e) => {
          openList();
          // Click runs after the browser places the caret (which collapses the
          // onFocus select-all), so re-select here whenever the user isn't
          // mid-query — typing should replace the selected label, not append.
          if (query === null) e.currentTarget.select();
        }}
      />
      {open && (
        <ul
          ref={listRef}
          id={`${id}-listbox`}
          role="listbox"
          aria-label="Options"
          className="menu combobox-list"
        >
          {filtered.length === 0 && <li className="combobox-empty">{emptyText}</li>}
          {filtered.map((opt, i) => (
            <li
              key={opt.value}
              id={`${id}-option-${i}`}
              role="option"
              aria-selected={opt.value === value}
              data-active={i === activeIndex ? 'true' : undefined}
              className="menu-item combobox-option"
              // preventDefault so the input doesn't blur (which would close
              // the list) before the click lands.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt)}
              onMouseMove={() => {
                if (i !== activeIndex) setActiveIndex(i);
              }}
            >
              <span className="combobox-option-label">{opt.label}</span>
              {opt.sublabel && <span className="combobox-option-sub">{opt.sublabel}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
